import { de } from './de.ts';
import { en } from './en.ts';
import { nl } from './nl.ts';
import { fr, type Catalogue, type MessageKey } from './fr.ts';

/** Interface languages. German (East Belgium) is an interface language only: drawings are in fr/en/nl. */
export type Locale = 'fr' | 'en' | 'nl' | 'de';
export type { MessageKey };

export const LOCALES: Record<Locale, { label: string; messages: Catalogue }> = {
  fr: { label: 'Français', messages: fr },
  en: { label: 'English', messages: en },
  nl: { label: 'Nederlands', messages: nl },
  de: { label: 'Deutsch', messages: de },
};

export type Translate = (key: MessageKey, params?: Record<string, string | number>) => string;

export function translator(locale: Locale): Translate {
  const messages = LOCALES[locale].messages;
  return (key, params) => {
    let s: string = messages[key] ?? fr[key] ?? key;
    if (params) for (const [k, v] of Object.entries(params)) s = s.replaceAll(`{${k}}`, String(v));
    return s;
  };
}

/** Pick the best supported locale from the browser/OS preferences. French by default. */
export function detectLocale(preferred: readonly string[] = []): Locale {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'fr' || base === 'en' || base === 'nl' || base === 'de') return base;
  }
  return 'fr';
}

/** BCP 47 tag for dates and numbers (Belgian French, Dutch and German, British English). */
export const localeTag = (l: Locale) =>
  l === 'fr' ? 'fr-BE' : l === 'nl' ? 'nl-BE' : l === 'de' ? 'de-BE' : 'en-GB';

/** Languages drawings, rule texts and symbol names exist in. */
export type ContentLocale = 'fr' | 'en' | 'nl';
/**
 * The language of texts that have no German version (symbol names, templates, AI answers…):
 * French for German, the second language of East Belgium.
 */
export const contentLocale = (l: Locale): ContentLocale => (l === 'de' ? 'fr' : l);

/** A text in the interface language, from a {fr, en, nl, de?} record (French when no German). */
export const pick = (t: { fr: string; en: string; nl: string; de?: string }, l: Locale): string =>
  l === 'de' ? (t.de ?? t.fr) : t[l];

/** `pick` for a text that may be missing (an unknown symbol or preset). */
export const pickMaybe = (
  t: { fr: string; en: string; nl: string; de?: string } | undefined,
  l: Locale,
): string | undefined => (t ? pick(t, l) : undefined);

/** An inline interface text in the four languages (for the few strings outside the catalogues). */
export const say = (l: Locale, fr: string, en: string, nl: string, de: string): string =>
  l === 'de' ? de : l === 'nl' ? nl : l === 'en' ? en : fr;
